import { ChevronDoubleDownIcon, ChevronDoubleUpIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { Checkbox, FormControl, ListItemText, MenuItem, Select } from "@material-ui/core";
import { handleProjectChange } from "./WorkLog3";
import { upload_file_to_s3, get_file_from_s3 } from "../../utils";
import { toast } from "react-toastify";


const WeekCard = (props) => {

    const {item,daysVisible,weekIndex,toggleDaysVisibility,datesData,getLogHoursValue,getCommentsValue,isEditable,isAdmin,handleCommentChange,handleHourChange,handleSubmit,timeSheet,setTimeSheet,weeklyTimesheetUploads} = props;

    const weekUpload = weeklyTimesheetUploads?.find(u => u.weekStartDate === item.firstDay) ?? null;

    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState({ fileName: "", file: null });
    const [s3Doc, setS3Doc] = useState(null);
    const [billingHoursInput, setBillingHoursInput] = useState("");
    const [descriptionInput, setDescriptionInput] = useState("");
    const [docLoader, setDocLoader] = useState(false);
    const [downloading, setDownloading] = useState(false);

    React.useEffect(() => {
      if (uploadOpen && weekUpload) {
        setBillingHoursInput(weekUpload.billingHours != null ? String(weekUpload.billingHours) : "");
        setDescriptionInput(weekUpload.description ?? "");
      }
    }, [uploadOpen]);

    const handleDownload = async (doc) => {
      setDownloading(true);
      try {
        const s3Url = await get_file_from_s3(doc?.fileName);
        if (!s3Url) { toast.error("Failed to fetch the file."); return; }
        const response = await fetch(s3Url);
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", doc?.originalFileName || "timesheet");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        toast.error("Failed to download file.");
        console.error(err);
      } finally {
        setDownloading(false);
      }
    };

    const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error("File size limit is 10MB"); return; }
      setUploadFile({ fileName: file.name, file });
      setS3Doc(null);
      setDocLoader(true);
      try {
        const s3path = await upload_file_to_s3(file);
        const s3Data = s3path?.data?.data;
        if (!s3Data?.fileName) {
          toast.error("File upload failed. Please try again.");
          setUploadFile({ fileName: "", file: null });
          return;
        }
        setS3Doc({ ...s3Data, originalFileName: file.name });
      } catch (err) {
        toast.error("File upload failed. Please try again.");
        setUploadFile({ fileName: "", file: null });
        console.error(err);
      } finally {
        setDocLoader(false);
      }
    };

    const exceedsWordLimit = (text) => {
      if (text) {
        const words = text.split("").length;
        return words > 25;
      }
      return false;
    };

  return (
    <div
      key={item?.firstDay.toString()}
      className="mx-auto w-[90%] rounded-xl bg-white border border-black mb-5"
    >
      <div
        className={`w-full h-fit flex justify-between bg-blue-100 ${
          daysVisible[weekIndex] ? "rounded-t-xl" : "rounded-xl"
        }`}
      >
        <h2 className="text-2xl font-semibold p-3 items-start">
          {item?.firstDay} to {item?.lastDay}
        </h2>

        <div className="flex items-center gap-4 my-auto mx-5">
          {weekUpload && (
            <span className="text-sm font-medium text-green-700 bg-green-100 border border-green-300 px-4 py-1.5 rounded-full">
              Timesheet Uploaded
            </span>
          )}
          {(!weekUpload || uploadOpen) && !isAdmin && (!timeSheet?.status || timeSheet?.status === 'DRAFT' || timeSheet?.status === 'REJECTED') && (
            <button
              onClick={() => {
                const opening = !uploadOpen;
                setUploadOpen(opening);
                if (opening && !daysVisible[weekIndex]) {
                  toggleDaysVisibility(weekIndex);
                }
              }}
              className="text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md"
            >
              {uploadOpen ? "Close Upload" : "Upload Timesheet"}
            </button>
          )}
          <button
            className="cursor-pointer"
            onClick={() => toggleDaysVisibility(weekIndex)}
          >
            {daysVisible[weekIndex] ? (
              <div className="flex space-x-5">
                <p className="text-lg text-blue-400">Close Timesheet</p>
                <ChevronDoubleUpIcon className="h-7 text-black cursor-pointer" />
              </div>
            ) : (
              <div className="flex space-x-5">
                <p className="text-lg text-blue-400">Fill Timesheet</p>
                <ChevronDoubleDownIcon className="h-7 text-black cursor-pointer" />
              </div>
            )}
          </button>
        </div>
      </div>

      <div className={`${daysVisible[weekIndex] ? "max-h-fit" : "hidden"}`}>
        {weekUpload && !uploadOpen && (
          <div className="px-6 py-5 border-b border-blue-100 bg-blue-50 flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-y-4">
              <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
                {/* Uploaded file */}
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-blue-500">Uploaded File</p>
                  <div className="flex items-center gap-3">
                    <p className="text-base font-semibold text-gray-800">
                      {weekUpload.timesheetDocument?.originalFileName ?? "—"}
                    </p>
                    {weekUpload.timesheetDocument && (
                      <button
                        onClick={() => handleDownload(weekUpload.timesheetDocument)}
                        disabled={downloading}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                      >
                        {downloading ? "Downloading..." : "Download"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="w-px h-10 bg-blue-200 hidden sm:block self-center" />

                {/* Effective hours */}
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-blue-500">Effective Working Hours</p>
                  <p className="text-base font-semibold text-gray-800">{weekUpload.billingHours ?? "—"} hrs</p>
                </div>
              </div>

              {(!timeSheet?.status || timeSheet?.status === 'DRAFT' || timeSheet?.status === 'REJECTED') && !isAdmin && (
                <button
                  onClick={() => setUploadOpen(true)}
                  className="text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md"
                >
                  Reupload Timesheet
                </button>
              )}
            </div>

            {/* Description on its own row */}
            {weekUpload.description && (
              <div className="flex flex-col gap-1.5">
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-500">Description</p>
                <p className="text-base font-semibold text-gray-800">{weekUpload.description}</p>
              </div>
            )}
          </div>
        )}
        {uploadOpen && isEditable && (
          <div className="px-6 py-4 border-b border-dashed border-gray-300 space-y-4 bg-gray-50">
            <h3 className="text-base font-semibold text-gray-700">Upload Timesheet</h3>

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600 min-w-[180px]">
                Effective Working Hours
              </span>
              <input
                type="number"
                min={0}
                value={billingHoursInput}
                onChange={(e) => setBillingHoursInput(e.target.value)}
                placeholder="Write in hours"
                className="w-48 h-[38px] border border-gray-300 rounded-md px-3 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600 min-w-[180px]">
                Timesheet File
              </span>
              <div className="relative w-60">
                <input
                  type="file"
                  onChange={handleFileUpload}
                  disabled={docLoader}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10 disabled:cursor-not-allowed"
                />
                <div className="w-60 h-[38px] px-3 border border-gray-300 rounded-md shadow-sm text-sm flex items-center justify-between bg-white">
                  <span className={`truncate ${uploadFile.fileName ? "text-gray-700" : "text-gray-400"}`}>
                    {uploadFile.fileName || (weekUpload?.timesheetDocument?.originalFileName ? `${weekUpload.timesheetDocument.originalFileName} (existing)` : "Choose a file...")}
                  </span>
                  {docLoader ? (
                    <div className="border-2 border-t-blue-500 border-gray-200 rounded-full w-4 h-4 animate-spin flex-shrink-0" />
                  ) : (
                    <span className="text-blue-500 text-xs flex-shrink-0 ml-1">Browse</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-600">Description</span>
              <textarea
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                placeholder="Enter description"
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        )}
        {datesData.length > 0 ? (
          datesData[weekIndex].map((item,index) => {
            const [day, month, year] = item.split("/");
            const date = new Date(year, month - 1, day);
            const currentDate = new Date(
              date.getTime() - date.getTimezoneOffset() * 60000
            );
           
             let logDataIndex = timeSheet?.employeeWorkLog?.findIndex(
               (y) => {
                 const formattedDate = new Date(
                   y.fromDate
                 ).toLocaleDateString("en-GB", {
                   day: "2-digit",
                   month: "2-digit",
                   year: "numeric",
                 });
                 return (
                   formattedDate === item || y.fromDate === item.split(".")[0]
                 );
               }
             );
            const reverseDate = (d) => {
              let inputDate = d;
              let date = new Date(inputDate);
              let day = String(date.getDate()).padStart(2, "0");
              let month = String(date.getMonth() + 1).padStart(2, "0");
              let year = date.getFullYear();
              let formattedDate = `${day}/${month}/${year}`;
              return formattedDate;
            };
            const showField = () => {
              const sons = props?.timeSheetDatesData?.filter((each) => {
                const reverseDatee = reverseDate(each.date);
                return item === reverseDatee;
              });
              return sons ? sons[0]?.status : 209;
            };
            const renderOtherData = (code) => {
              switch (code) {
                case 202:
                  return (
                    <p className="text-center font-semibold">Absent on this day</p>
                  );
                case 203:
                  return (
                    <p className="text-center font-semibold">WeekOFF</p>
                  );
                case 204:
                  return (
                    <p className="text-center font-semibold">On leave</p>
                  );
                case 205:
                  return (
                    <p className="text-center font-semibold">Holiday</p>
                  );
                case 207:
                    return (
                    <p className="text-center font-semibold"></p>
                    );
                case 209:
                  return (
                    <p>Record Not Found</p>
                  );
                default:
                  return null;
              }              
            };
            const renderDate = () => {
              const statuss = showField();
              if (statuss === 201 || statuss === 206 || statuss === undefined) {
                return (
                  <>
                    <div>
                      <p className="text-[10px] mb-2">Select Projects:</p>
                      {props.projects?.length > 0 ? (
                        <FormControl className="w-full mt-2">
                          <Select
                            label="Projects"
                            name="projects"
                            className="w-full"
                            inputProps={{ "aria-label": "Without label" }}
                            displayEmpty
                            multiple
                            value={props?.projectId?.[logDataIndex] ?? []}
                            onChange={(e) =>
                              props.handleProjects(e, logDataIndex)
                            }
                            // disabled={isSubmitted}
                            MenuProps={{
                              sx: {
                                "& .MuiList-root": {
                                  maxHeight: 300,
                                },
                                "& .MuiInputBase-root": {
                                  height: 50,
                                },
                              },
                            }}
                            //disabled={isApplied}
                            renderValue={(selected) => {
                              return selected
                                .map((value) => {
                                  const selectedItem = props.projects.find(
                                    (item) =>
                                      item._id === value ||
                                      item.project === value
                                  );
                                  return selectedItem
                                    ? selectedItem.project
                                    : "";
                                })
                                .join(", ");
                            }}
                          >
                            {props.projects.length > 0 &&
                              props.projects.map((x) => {
                                return (
                                  <MenuItem key={x._id} value={x._id}>
                                    <Checkbox
                                      checked={timeSheet?.employeeWorkLog[
                                        logDataIndex
                                      ]?.projectIds?.includes(x._id)}
                                      // Use includes for better readability
                                      onClick={(e) =>
                                        handleProjectChange(
                                          item,
                                          timeSheet,
                                          setTimeSheet,
                                          x._id,
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <ListItemText
                                      primary={x.project}
                                      onClick={() => {
                                        const isCurrentlyChecked =
                                          timeSheet?.employeeWorkLog[
                                            logDataIndex
                                          ]?.projectIds?.includes(x._id);
                                        handleProjectChange(
                                          item,
                                          timeSheet,
                                          setTimeSheet,
                                          x._id,
                                          !isCurrentlyChecked
                                        );
                                      }}
                                      style={{ cursor: "pointer" }}
                                    />{" "}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>
                      ) : (
                        "No Project/On Bench"
                      )}
                    </div>
                    <div>
                      <p className="text-[10px]">Enter log hours:</p>
                      <input
                        id={currentDate.toISOString()}
                        placeholder="Enter working hours"
                        className="w-full px-3 py-2 border rounded-sm bg-gray-100"
                        onChange={(e) =>
                          handleHourChange(e, timeSheet, setTimeSheet)
                        }
                        value={getLogHoursValue(
                          currentDate.toISOString(),
                          timeSheet
                        )}
                        type="number"
                      />
                    </div>
                    <div>
                      <p className="text-[10px]">Enter Task:</p>
                      <input
                        id={currentDate.toISOString()}
                        className="w-full px-3 py-2 border rounded-sm bg-gray-100"
                        onChange={(e) =>
                          handleCommentChange(e, timeSheet, setTimeSheet)
                        }
                        value={getCommentsValue(
                          currentDate.toISOString(),
                          timeSheet
                        )}
                        type="text"
                      />
                    </div>
                  </>
                );
              } else {
                return renderOtherData(statuss);
              }
            };
            const renderNonEditbleData = () => {
              const statuss = showField();
              if (statuss === 201 || statuss === 206 || statuss === undefined) {
                return (
                  <>
                    <p className="text-lg my-auto">
                      Projects :{" "}
                      {props?.projectId?.[logDataIndex]?.length > 0
                        ? props?.projectId?.[logDataIndex].join(",")
                        : "-"}
                    </p>
                    <p className="text-lg my-auto">
                      Worked Hours :{" "}
                      {getLogHoursValue(currentDate.toISOString(), timeSheet)}
                    </p>
                    <p className="text-lg my-auto">
                      Tasks :{" "}
                      <Tooltip
                        title={getCommentsValue(
                          currentDate.toISOString(),
                          timeSheet
                        )}
                        arrow
                        placement="top"
                      >
                        <span className="text-sm">
                          {exceedsWordLimit(
                            getCommentsValue(
                              currentDate.toISOString(),
                              timeSheet
                            )
                          )
                            ? getCommentsValue(
                                currentDate.toISOString(),
                                timeSheet
                              ).slice(0, 25) + "..."
                            : getCommentsValue(
                                currentDate.toISOString(),
                                timeSheet
                              ) || "--"}
                        </span>
                      </Tooltip>
                    </p>
                  </>
                );
              } else {
                return renderOtherData(statuss);
              }
            };
            return (
              <div
                key={item}
                className={`grid grid-cols-4 gap-2 col-span-2 px-6 py-5 overflow-hidden border-t-2`}
              >
                <p className="text-xl font-semibold my-auto ">{item}</p>
                {isEditable ? renderDate() : renderNonEditbleData()}
              </div>
            );
          })
        ) : (
          <></>
        )}
        <div
          className={` justify-end my-5 w-[95%] ${isEditable && !isAdmin ? "flex" : "hidden"}`}
        >
          <button
            className="bg-blue-500 hover:bg-white hover:text-blue-500 border border-blue-500 text-white font-bold py-1 px-2 rounded"
            onClick={() => {
              const uploadData = uploadOpen && (s3Doc || weekUpload?.timesheetDocument) ? {
                timesheetDocument: s3Doc ?? weekUpload.timesheetDocument,
                billingHours: billingHoursInput,
                description: descriptionInput,
                weekStartDate: item.firstDay,
                weekEndDate: item.lastDay,
              } : null;
              handleSubmit(false, uploadData);
              if (uploadOpen) {
                setUploadOpen(false);
                setUploadFile({ fileName: "", file: null });
                setS3Doc(null);
                setBillingHoursInput("");
                setDescriptionInput("");
              }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeekCard;
